import {
  sessionStorageSchema,
  type SessionStorageData,
  type SessionStorageKey,
} from './schema';

/**
 * Safe wrapper around sessionStorage with runtime type checking
 * Ensures data integrity and prevents runtime errors from invalid storage data
 */
class SafeSessionStorage {
  private readonly available: boolean;

  constructor() {
    this.available = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    try {
      const test = '__session_storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get a value from session storage with type safety
   */
  getItem<K extends SessionStorageKey>(key: K): SessionStorageData[K] | null {
    if (!this.available) {
      console.warn('Session storage is not available');
      return null;
    }

    try {
      const item = sessionStorage.getItem(key);
      if (!item) return null;

      const parsed = JSON.parse(item);
      const schema = sessionStorageSchema.shape[key];

      if (!schema) {
        console.error(`No schema defined for key: ${key}`);
        return null;
      }

      const validated = schema.safeParse(parsed);

      if (!validated.success) {
        console.error(
          `Invalid session storage data for key "${key}":`,
          validated.error.format()
        );
        // Clean up invalid data
        this.removeItem(key);
        return null;
      }

      return validated.data as SessionStorageData[K];
    } catch (error) {
      console.error(`Error reading from session storage (${key}):`, error);
      // Clean up corrupted data
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Set a value in session storage with type safety
   */
  setItem<K extends SessionStorageKey>(
    key: K,
    value: SessionStorageData[K]
  ): boolean {
    if (!this.available) {
      console.warn('Session storage is not available');
      return false;
    }

    try {
      const schema = sessionStorageSchema.shape[key];

      if (!schema) {
        console.error(`No schema defined for key: ${key}`);
        return false;
      }

      const validated = schema.safeParse(value);

      if (!validated.success) {
        console.error(
          `Invalid data for session storage key "${key}":`,
          validated.error.format()
        );
        return false;
      }

      sessionStorage.setItem(key, JSON.stringify(validated.data));
      return true;
    } catch (error) {
      console.error(`Error writing to session storage (${key}):`, error);
      return false;
    }
  }

  /**
   * Remove a value from session storage
   */
  removeItem(key: SessionStorageKey): void {
    if (!this.available) return;

    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from session storage (${key}):`, error);
    }
  }

  /**
   * Clear all session storage data
   */
  clear(): void {
    if (!this.available) return;

    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing session storage:', error);
    }
  }
}

export const safeSessionStorage = new SafeSessionStorage();
